import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Smartphone, 
  Monitor, 
  Watch, 
  Tablet, 
  Cpu, 
  Layers, 
  Zap, 
  ArrowUpRight, 
  CheckCircle,
  ArrowRight,
  Code2,
  Terminal,
  Palette
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const AppleDev = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    document.title = 'iOS & macOS Development | Squarex Studios';

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo('.apple-hero-title', 
        { opacity: 0, y: 80 }, 
        { opacity: 1, y: 0, duration: 1.2, ease: 'power4.out' }
      );
      gsap.fromTo('.apple-hero-subtitle', 
        { opacity: 0, y: 40 }, 
        { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' }
      );
      gsap.fromTo('.apple-hero-visual', 
        { opacity: 0, scale: 0.9 }, 
        { opacity: 1, scale: 1, duration: 1.5, delay: 0.5, ease: 'power2.out' }
      );

      // Section reveal
      const sections = document.querySelectorAll('.apple-section');
      sections.forEach((section) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 50 }, 
          { 
            opacity: 1, 
            y: 0, 
            duration: 1, 
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
            }
          }
        );
      });

      // Feature cards
      gsap.fromTo('.feature-card', 
        { opacity: 0, y: 30 }, 
        { 
          opacity: 1, 
          y: 0, 
          duration: 0.8, 
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.features-grid',
            start: 'top 75%',
          }
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="apple-dev-page" className="bg-[#0F0F0F] text-white overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-32 pb-20 hero-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div className="z-10">
            <div className="inline-flex items-center gap-2 bg-[#af0000]/20 text-[#af0000] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Zap size={16} />
              Premium Apple Ecosystem Development
            </div>
            <h1 className="apple-hero-title text-5xl md:text-7xl lg:text-8xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-8">
              Native <span className="text-[#af0000]">Performance.</span><br />
              Elegant Design.
            </h1>
            <p className="apple-hero-subtitle text-xl md:text-2xl text-[#94a3b8] font-light max-w-xl mb-10 leading-relaxed">
              We craft exceptional experiences specifically for the Apple ecosystem. From high-performance macOS applications to feature-rich iOS & iPadOS apps.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#services" className="btn-primary inline-flex items-center justify-center gap-2">
                Our Ecosystem
                <ArrowRight size={18} />
              </a>
              <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
                Start Your App
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
          
          <div className="apple-hero-visual relative flex justify-center items-center">
            {/* Visual representation of Apple devices */}
            <div className="relative w-full max-w-md aspect-square">
              <div className="absolute inset-0 bg-[#af0000]/10 blur-[120px] rounded-full animate-pulse" />
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4 pt-12">
                  <DeviceIcon icon={Monitor} label="macOS" color="bg-blue-500/10" iconColor="text-blue-500" />
                  <DeviceIcon icon={Smartphone} label="iOS" color="bg-red-500/10" iconColor="text-red-500" />
                </div>
                <div className="space-y-4">
                  <DeviceIcon icon={Tablet} label="iPadOS" color="bg-purple-500/10" iconColor="text-purple-500" />
                  <DeviceIcon icon={Watch} label="watchOS" color="bg-orange-500/10" iconColor="text-orange-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Apps Section */}
      <section className="py-24 md:py-32 bg-[#141414]/50 border-t border-white/10 apple-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Internal Products</p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">Our <span className="text-[#af0000]">Apps</span></h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <Link to="/app/coluro" className="group relative bg-white/5 border border-white/10 rounded-3xl p-8 overflow-hidden transition-all hover:bg-white/10 hover:border-[#4F8EF7]/50">
              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
                <img 
                  src="/coluro/Coluro-iOS-Default-1024x1024@1x.png" 
                  alt="Coluro" 
                  className="w-32 h-32 rounded-3xl shadow-2xl transition-transform group-hover:scale-105" 
                />
                <div>
                  <div className="inline-flex items-center gap-2 text-[#4F8EF7] text-xs font-bold uppercase tracking-widest mb-3">
                    <Monitor size={14} />
                    macOS Utility
                  </div>
                  <h3 className="text-3xl font-bold font-['Syne'] mb-4 group-hover:text-[#4F8EF7] transition-colors">Coluro</h3>
                  <p className="text-[#94a3b8] mb-6">The fastest, most precise color picker for Mac. Built for designers and developers who value their workflow speed.</p>
                  <div className="flex items-center gap-2 text-white font-semibold">
                    View Product <ArrowRight size={18} />
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                <Palette size={120} className="text-[#4F8EF7]" />
              </div>
            </Link>

            <div className="bg-white/5 border border-white/10 border-dashed rounded-3xl p-8 flex flex-col items-center justify-center text-center">
               <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6">
                  <Zap size={32} className="text-[#64748b]" />
               </div>
               <h3 className="text-2xl font-bold font-['Syne'] text-[#64748b] mb-2">More coming soon</h3>
               <p className="text-[#475569] max-w-xs">We're constantly building tools to improve the digital creator's workflow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">The Tech Stack</p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-8">
                Built with <span className="text-[#af0000]">Swift</span> at the core
              </h2>
              <div className="space-y-6">
                <TechItem title="Swift & SwiftUI" desc="Modern, fast, and safe. We use Apple's latest UI framework to build fluid interfaces." />
                <TechItem title="Metal & Core Image" desc="High-performance graphics and image processing for demanding applications." />
                <TechItem title="Combine & Async/Await" desc="Efficient data flow and asynchronous programming for responsive UX." />
                <TechItem title="Apple Silicon Optimization" desc="Ensuring your apps run natively and efficiently on M1, M2, and beyond." />
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 md:p-12 rounded-2xl">
               <div className="flex items-center gap-3 mb-8">
                 <div className="w-12 h-12 bg-[#af0000] flex items-center justify-center rounded-lg">
                   <Terminal className="text-white" size={24} />
                 </div>
                 <h3 className="text-2xl font-bold font-['Syne']">Performance First</h3>
               </div>
               <p className="text-[#94a3b8] leading-relaxed mb-8">
                 We don't just build apps; we engineer them. Our process focuses on memory efficiency, thread safety, and energy conservation, ensuring your users get the best possible experience without compromising device performance.
               </p>
               <div className="grid grid-cols-2 gap-4">
                 <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                   <div className="text-[#af0000] font-bold text-2xl mb-1">99.9%</div>
                   <div className="text-xs text-[#94a3b8] uppercase tracking-wider">Crash-Free Rate</div>
                 </div>
                 <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                   <div className="text-[#af0000] font-bold text-2xl mb-1">&lt;100ms</div>
                   <div className="text-xs text-[#94a3b8] uppercase tracking-wider">Launch Time</div>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#af0000]/5" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold font-['Syne'] tracking-tight mb-8">
            Have an idea for the <span className="text-[#af0000]">Apple Store?</span>
          </h2>
          <p className="text-xl text-[#94a3b8] mb-12">
            Whether it's a productivity tool for macOS or a lifestyle app for iOS, we have the expertise to bring it to life with premium quality.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 px-10 py-5 text-lg">
            Start Your Project <ArrowUpRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

const DeviceIcon = ({ icon: Icon, label, color, iconColor }) => (
  <div className={`p-8 ${color} border border-white/10 rounded-3xl flex flex-col items-center justify-center gap-4 transition-transform hover:scale-105 duration-300`}>
    <Icon className={iconColor} size={48} />
    <span className="font-semibold text-sm tracking-widest uppercase">{label}</span>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="feature-card group p-8 bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-500">
    <Icon size={32} className="text-[#af0000] mb-6" />
    <h3 className="text-xl font-bold font-['Syne'] mb-3">{title}</h3>
    <p className="text-[#94a3b8] text-sm leading-relaxed">{desc}</p>
  </div>
);

const TechItem = ({ title, desc }) => (
  <div className="flex gap-4">
    <CheckCircle className="text-[#af0000] flex-shrink-0 mt-1" size={20} />
    <div>
      <h4 className="font-bold text-lg mb-1">{title}</h4>
      <p className="text-[#94a3b8] text-sm">{desc}</p>
    </div>
  </div>
);

export default AppleDev;
